import { useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { useTheme } from 'foundations-theming/utils';
import { useIsPillBorderRadius } from 'portals-platform-lib';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { Button } from 'foundations-components/transitional/components/Button';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { UserTicketStatusOption, UserTicketViewOption } from '../../data/types';
import { DateFilterType, getDateFilterLabel } from '../../constants/filters';
import { useHasCompanyPermissions } from '../../hooks/useHasCompanyPermissions';
import { H2 } from 'foundations-components/transitional/components/H2';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EXPORT_LIMIT = 5000;
const getViewLabel = view => {
  switch (view) {
    case UserTicketViewOption.PERSONAL:
      return I18n.text('customer-portal-app.view.my');
    case UserTicketViewOption.COMPANY:
      return I18n.text('customer-portal-app.view.all');
    default:
      return view;
  }
};
const getStatusLabel = status => {
  switch (status) {
    case UserTicketStatusOption.OPEN:
      return I18n.text('customer-portal-app.status.open');
    case UserTicketStatusOption.CLOSED:
      return I18n.text('customer-portal-app.status.closed');
    default:
      return null;
  }
};
function FilterList({
  showView,
  ticketView,
  statusFilter,
  createDateFilter,
  lastActivityFilter,
  searchString
}) {
  const statusLabel = getStatusLabel(statusFilter);
  const trimmedSearch = searchString.trim();
  const hasCreatedFilter = createDateFilter !== DateFilterType.ALL_TIME;
  const hasLastActivityFilter = lastActivityFilter !== DateFilterType.ALL_TIME;
  if (!showView && !statusLabel && !hasCreatedFilter && !hasLastActivityFilter && !trimmedSearch) {
    return null;
  }
  return /*#__PURE__*/_jsxs(List, {
    children: [showView && /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.ticketListings.export.modal.viewLine",
        options: {
          viewName: getViewLabel(ticketView)
        }
      })
    }), statusLabel && /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.ticketListings.export.modal.statusLine",
        options: {
          status: statusLabel
        }
      })
    }), hasCreatedFilter && /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.ticketListings.export.modal.createdLine",
        options: {
          dateRange: getDateFilterLabel(createDateFilter)
        }
      })
    }), hasLastActivityFilter && /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.ticketListings.export.modal.lastActivityLine",
        options: {
          dateRange: getDateFilterLabel(lastActivityFilter)
        }
      })
    }), trimmedSearch && /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customer-portal-app.ticketListings.export.modal.searchLine",
        options: {
          searchTerm: trimmedSearch
        }
      })
    })]
  });
}
export function ExportConfirmModal({
  onConfirm,
  onClose,
  ticketView,
  statusFilter,
  createDateFilter,
  lastActivityFilter,
  searchString,
  totalCount
}) {
  const isUsingPillBorderRadius = useIsPillBorderRadius();
  const hasCompanyPermissions = useHasCompanyPermissions();
  const theme = useTheme();
  const overrideTheme = useMemo(() => Object.assign({}, theme, {
    borderRadius: Object.assign({}, theme.borderRadius, {
      'transitional-floating': theme.borderRadius['0']
    })
  }), [theme]);
  const content = /*#__PURE__*/_jsxs(Modal, {
    size: "auto",
    onEsc: onClose,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.ticketListings.export.modal.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        gap: "200",
        children: [/*#__PURE__*/_jsx(FilterList, {
          showView: hasCompanyPermissions,
          ticketView: ticketView,
          statusFilter: statusFilter,
          createDateFilter: createDateFilter,
          lastActivityFilter: lastActivityFilter,
          searchString: searchString
        }), /*#__PURE__*/_jsx(Text, {
          color: "text-core-subtle",
          children: totalCount >= EXPORT_LIMIT ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketListings.export.modal.limitNote"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customer-portal-app.ticketListings.export.modal.countNote",
            options: {
              count: totalCount
            }
          })
        })]
      })
    }), /*#__PURE__*/_jsxs(DialogFooter, {
      children: [/*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: onConfirm,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.ticketListings.export.modal.confirm"
        })
      }), /*#__PURE__*/_jsx(Button, {
        use: "secondary-alt",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customer-portal-app.ticketListings.export.modal.cancel"
        })
      })]
    })]
  });
  if (!isUsingPillBorderRadius) return content;
  return /*#__PURE__*/_jsx(ThemeProvider, {
    theme: overrideTheme,
    children: content
  });
}